import { GidType, toGid } from "@redotech/redo-shopify-common/gid-utils";
import { REDO_HSA_PAYMENT_PRIORITY_ATTRIBUTE_KEY } from "@redotech/redo-storefront-common/cart-attributes";
import { StorefrontClient } from "../client/storefront";
import { log } from "../monitoring/log";
import {
  createCartAndCheckout,
  goToHsaStandardCheckout,
} from "../utils/checkout-buttons/on-click-logic";
import { extractProductFormData } from "../utils/use-buy-now-coverage";
import { registerRedoHsaApi } from "./window-api";

/**
 * Coverage behavior the returns-coverage manager island contributes when it
 * mounts. Absent on HSA-only stores, so checkout proceeds coverage-free.
 */
export interface HsaCoverageContributions {
  /** Redo coverage variant (plain id) to attach for a buy-now of `variantId` x
   *  `quantity`, or null when coverage doesn't apply. */
  resolveBuyNowCoverageVariantId?: (
    variantId: string | number | undefined,
    quantity: number,
  ) => string | null;
  /** Take the existing cart to HSA checkout via the Checkout+ coverage reconcile
   *  path (opt-in state owned by the manager). */
  goToHsaExistingCartCheckout?: () => Promise<void>;
}

let coverageContributions: HsaCoverageContributions = {};

export function setHsaCoverageContributions(
  contributions: HsaCoverageContributions,
): void {
  coverageContributions = contributions;
}

export function clearHsaCoverageContributions(): void {
  coverageContributions = {};
}

/**
 * Pure builder for the `createCartAndCheckout` params of an HSA buy-now — kept
 * client/navigation-free so it can be unit-tested directly.
 */
export function buildHsaBuyNowCheckoutParams({
  variantId,
  quantity,
  coverageVariantId,
}: {
  variantId: string | number;
  quantity: number;
  coverageVariantId: string | null;
}): {
  productLines: { merchandiseId: string; quantity: number }[];
  coverageVariantId: string | null;
  extraAttributes: { key: string; value: string }[];
  destination: "hsa-standard";
} {
  return {
    productLines: [
      { merchandiseId: toGid(GidType.PRODUCT_VARIANT, variantId), quantity },
    ],
    coverageVariantId,
    extraAttributes: [
      { key: REDO_HSA_PAYMENT_PRIORITY_ATTRIBUTE_KEY, value: "true" },
    ],
    destination: "hsa-standard",
  };
}

/** Checkout primitives, injectable so the handlers are testable without a live
 *  `location` / navigation. */
export interface HsaCheckoutApiDeps {
  createCartAndCheckout: typeof createCartAndCheckout;
  goToHsaStandardCheckout: typeof goToHsaStandardCheckout;
}

/**
 * Builds the imperative HSA checkout handlers over a client. Split from
 * registration so the handler logic is unit-testable without `window`.
 */
export function createHsaCheckoutApi(
  client: StorefrontClient,
  deps: HsaCheckoutApiDeps = { createCartAndCheckout, goToHsaStandardCheckout },
): {
  goToHsaCheckout: () => Promise<void>;
  buyProductNowWithHsa: (opts?: {
    variantId?: string;
    quantity?: number;
  }) => Promise<void>;
} {
  const goToHsaCheckout = async (): Promise<void> => {
    const existingCartCheckout =
      coverageContributions.goToHsaExistingCartCheckout;
    if (existingCartCheckout) {
      await existingCartCheckout();
      return;
    }
    await deps.goToHsaStandardCheckout(client);
  };

  const buyProductNowWithHsa = async (opts?: {
    variantId?: string;
    quantity?: number;
  }): Promise<void> => {
    const { variantId, quantity } = opts?.variantId
      ? { variantId: opts.variantId, quantity: opts.quantity ?? 1 }
      : extractProductFormData();
    if (!variantId || String(variantId) === "0") {
      return;
    }
    // Public API: an external caller (or a malformed PDP quantity input) can
    // hand us a non-positive or non-integer quantity. Reject it up front.
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      log("hsa buy now: invalid quantity", { quantity });
      return;
    }
    const coveragePlainId =
      coverageContributions.resolveBuyNowCoverageVariantId?.(
        variantId,
        quantity,
      ) ?? null;
    const coverageVariantId = coveragePlainId
      ? toGid(GidType.PRODUCT_VARIANT, coveragePlainId)
      : null;
    await deps.createCartAndCheckout({
      storefrontClient: client,
      ...buildHsaBuyNowCheckoutParams({
        variantId,
        quantity,
        coverageVariantId,
      }),
    });
  };

  return { goToHsaCheckout, buyProductNowWithHsa };
}

/**
 * Registers the imperative HSA checkout API on `window.RedoHsa`, owned by the
 * HSA island so it works even when returns-coverage is disabled.
 */
export function registerHsaCheckoutApi(client: StorefrontClient): void {
  registerRedoHsaApi(createHsaCheckoutApi(client));
}
